import * as StoreIdDecorator from "../decorators/StoreIdDecorator";

export { AbstractGenericStore, GenericStore };

/**
 * K = the unique identifier on the class. UUID or Numbers are appropriate
 * T = the Class we're storing
 */

interface GenericStore<K, T> {
  lookup(key: K): Promise<T | undefined>;
  get(id: K): Promise<T | undefined>;
  getMany(ids: K[]): Promise<T | undefined>[];
  getAll(): Array<T>;
  set(value: T, overwrite: boolean): T;
  setMany(values: T[], overwrite: boolean): T[];
}
abstract class AbstractGenericStore<
  K,
  T extends object,
> implements GenericStore<K, T> {
  // TODO: make Dict or Set, would be faster/less complex
  protected values: T[];
  private valuePromises: Map<K, Promise<T | undefined>>;

  constructor() {
    this.values = [];
    this.valuePromises = new Map();
  }
  /**
   * Lookup the object from the API
   */
  public abstract lookup(key: K): Promise<T | undefined>;

  /**
   * Returns an immutable list of T. Why am I not using Object.freeze here ????
   */
  getAll(): Array<T> {
    return this.values;
  }

  async get(id: K): Promise<T | undefined> {
    const value: T | null =
      this.values.filter((x) => this.getStoredObjectKeyValue(x) === id)[0] ??
      null;
    if (value) {
      return value;
    }

    if (this.valuePromises.has(id)) {
      return this.valuePromises.get(id);
    }
    const newPromise: Promise<T | undefined> = this.lookup(id);
    this.valuePromises.set(id, newPromise);
    let returnRole: T | undefined = undefined;
    newPromise.then((r) => {
      if (r != undefined) {
        this.values.push(r);
        returnRole = r;
      }
    });
    newPromise.finally(() => {
      this.valuePromises.delete(id);
    });
    await newPromise;
    return returnRole;
  }

  getMany(ids: K[]): Promise<T | undefined>[] {
    return ids.map((id) => this.get(id));
  }

  /**
   * Method relies on reflection, negatively affects performance.
   * @param ids
   * @returns
   */
  public getWithoutLookup(id: K): T | undefined {
    return this.values.filter((x) =>
      this.storedObjectKeysEquals(this.getStoredObjectKeyValue(x), id),
    )[0];
  }

  public getManyWithoutLookup(ids: K[]): Array<T | undefined> {
    return ids.map((id) => this.getWithoutLookup(id));
  }

  /**
   * Updates the Store values array
   * @param value The Object to store
   * @param overwrite By default, the pre-existing object will not be overwritten by the new value proeprty
   */
  set(value: T, overwrite: boolean = false): T {
    // const id: string = StoreIdDecorator.getStoreIdProperty(value);
    const valueId: K = this.getStoredObjectKeyValue(value);
    // this could be removed by using a Set for the values property
    let index: number = this.values.findIndex((x) =>
      this.storedObjectKeysEquals(this.getStoredObjectKeyValue(x), valueId),
    );
    let removeCount: number = 1;
    if (index == -1) {
      // TODO: would allocating to end of the array be faster ?
      // if our object doesn't exist, insert at start of array.
      removeCount = 0;
      index = 0;
    } else if (!overwrite) {
      // if the object already existed and we didn't want to overwrite it, just return the existing object
      return this.values[index];
    }
    // insert or update the object
    this.values.splice(index, removeCount, value);
    return value;
  }

  setMany(values: T[], overwrite: boolean = false): T[] {
    return values.map((v) => this.set(v, overwrite));
  }

  protected getStoredObjectKeyValue(value: T): K {
    const keyProperty: string = StoreIdDecorator.getStoreIdProperty(value);
    //@ts-expect-error
    const key: K = value[keyProperty];
    return key;
  }

  abstract storedObjectKeysEquals(a: K, b: K): boolean;
}
